import { useState, useMemo, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Flame, CalendarDays, Dumbbell, Trophy, Star, Clock, ChevronDown, ChevronUp, Weight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { ExerciseData, WorkoutSessionRecord } from "@/hooks/useWorkoutSessions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { format, startOfDay, differenceInDays, startOfWeek, endOfWeek, differenceInWeeks, isSameWeek, subWeeks } from "date-fns";

interface WorkoutCalendarProps {
  sessions: WorkoutSessionRecord[];
  totalWorkouts: number;
}

interface WeeklyMilestone {
  week: number;
  workouts: number;
  achieved: boolean;
}

export function WorkoutCalendar({ sessions, totalWorkouts }: WorkoutCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [showDayDetail, setShowDayDetail] = useState(false);
  const [lastCelebratedWeek, setLastCelebratedWeek] = useState<number>(() => {
    const stored = localStorage.getItem('lastCelebratedWeek');
    return stored ? parseInt(stored, 10) : 0;
  });

  // Create a map of dates to workout sessions
  const workoutDatesMap = useMemo(() => {
    const map = new Map<string, WorkoutSessionRecord[]>();
    sessions.forEach(session => {
      const dateKey = format(new Date(session.completed_at), 'yyyy-MM-dd');
      const existing = map.get(dateKey) || [];
      map.set(dateKey, [...existing, session]);
    });
    return map;
  }, [sessions]);

  // Calculate week streaks (consecutive weeks with at least one workout)
  const { currentStreak, longestStreak } = useMemo(() => {
    if (sessions.length === 0) return { currentStreak: 0, longestStreak: 0 };

    // Get unique weeks with workouts (using start of week as key)
    const weeksWithWorkouts = new Set<string>();
    sessions.forEach(s => {
      const weekStart = startOfWeek(new Date(s.completed_at), { weekStartsOn: 1 });
      weeksWithWorkouts.add(format(weekStart, 'yyyy-MM-dd'));
    });

    if (weeksWithWorkouts.size === 0) return { currentStreak: 0, longestStreak: 0 };

    // Sort weeks descending (most recent first)
    const sortedWeeks = [...weeksWithWorkouts].sort().reverse();
    
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    const lastWeekStart = subWeeks(currentWeekStart, 1);
    
    // Check if current week or last week has a workout (streak is active)
    const hasCurrentWeekWorkout = weeksWithWorkouts.has(format(currentWeekStart, 'yyyy-MM-dd'));
    const hasLastWeekWorkout = weeksWithWorkouts.has(format(lastWeekStart, 'yyyy-MM-dd'));
    
    // If neither current nor last week has workout, streak is 0
    if (!hasCurrentWeekWorkout && !hasLastWeekWorkout) {
      // Just calculate longest streak from history
      let longest = 1;
      let current = 1;
      for (let i = 1; i < sortedWeeks.length; i++) {
        const prevWeek = new Date(sortedWeeks[i - 1]);
        const currWeek = new Date(sortedWeeks[i]);
        const weekDiff = differenceInWeeks(prevWeek, currWeek);
        if (weekDiff === 1) {
          current++;
          longest = Math.max(longest, current);
        } else {
          current = 1;
        }
      }
      return { currentStreak: 0, longestStreak: longest };
    }

    // Calculate current streak starting from current or last week
    let streak = 0;
    let checkWeek = hasCurrentWeekWorkout ? currentWeekStart : lastWeekStart;
    
    while (weeksWithWorkouts.has(format(checkWeek, 'yyyy-MM-dd'))) {
      streak++;
      checkWeek = subWeeks(checkWeek, 1);
    }

    // Calculate longest streak
    let longest = streak;
    let tempStreak = 1;
    
    for (let i = 1; i < sortedWeeks.length; i++) {
      const prevWeek = new Date(sortedWeeks[i - 1]);
      const currWeek = new Date(sortedWeeks[i]);
      const weekDiff = differenceInWeeks(prevWeek, currWeek);
      
      if (weekDiff === 1) {
        tempStreak++;
        longest = Math.max(longest, tempStreak);
      } else {
        tempStreak = 1;
      }
    }

    return { currentStreak: streak, longestStreak: Math.max(longest, streak) };
  }, [sessions]);

  // Calculate weekly stats
  const weeklyStats = useMemo(() => {
    const thisWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const thisWeekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
    
    const thisWeekWorkouts = sessions.filter(s => {
      const date = new Date(s.completed_at);
      return date >= thisWeekStart && date <= thisWeekEnd;
    }).length;

    // Calculate completed weeks (7+ days of workout history)
    const sortedDates = [...new Set(
      sessions.map(s => format(new Date(s.completed_at), 'yyyy-MM-dd'))
    )].sort();
    
    let completedWeeks = 0;
    if (sortedDates.length > 0) {
      const firstWorkout = new Date(sortedDates[0]);
      const daysSinceFirst = differenceInDays(new Date(), firstWorkout);
      completedWeeks = Math.floor(daysSinceFirst / 7);
    }

    return { thisWeekWorkouts, completedWeeks };
  }, [sessions]);

  // Weekly milestone celebration
  useEffect(() => {
    const { completedWeeks } = weeklyStats;
    if (completedWeeks > lastCelebratedWeek && completedWeeks > 0) {
      // Celebrate the new week milestone
      const weekMessages = [
        { week: 1, message: "Week 1 complete! You're building momentum! 🔥" },
        { week: 2, message: "2 weeks strong! Keep crushing it! 💪" },
        { week: 3, message: "3 weeks in! You're unstoppable! 🚀" },
        { week: 4, message: "1 month of gains! You're a beast! 🏆" },
      ];
      
      const milestone = weekMessages.find(m => m.week === completedWeeks) || 
        { message: `Week ${completedWeeks} complete! Legendary consistency! 👑` };
      
      setTimeout(() => {
        toast.success(milestone.message, {
          duration: 5000,
          icon: <Trophy className="w-5 h-5 text-yellow-500" />,
        });
      }, 1000);
      
      setLastCelebratedWeek(completedWeeks);
      localStorage.setItem('lastCelebratedWeek', completedWeeks.toString());
    }
  }, [weeklyStats.completedWeeks, lastCelebratedWeek]);

  // Get workouts for selected date
  const selectedDateWorkouts = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    return workoutDatesMap.get(dateKey) || [];
  }, [selectedDate, workoutDatesMap]);

  const handleDayClick = (date: Date | undefined) => {
    if (!date) return;
    setSelectedDate(date);
    const dateKey = format(date, 'yyyy-MM-dd');
    if (workoutDatesMap.has(dateKey)) {
      setShowDayDetail(true);
    }
  };

  // Custom day content to show workout indicators
  const modifiers = {
    hasWorkout: (date: Date) => {
      const dateKey = format(date, 'yyyy-MM-dd');
      return workoutDatesMap.has(dateKey);
    },
  };

  const modifiersStyles = {
    hasWorkout: {
      fontWeight: 'bold',
    },
  };

  return (
    <section className="glass-card rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-primary" />
          Workout Calendar
        </h2>
        <div className="flex items-center gap-3">
          {/* Current Streak */}
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-orange-500/20 to-red-500/20 px-3 py-1.5 rounded-full">
            <Flame className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-bold">{currentStreak}</span>
            <span className="text-xs text-muted-foreground">week streak</span>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-secondary/30 rounded-xl p-3 text-center">
          <p className="text-2xl font-display font-bold text-primary">{currentStreak}</p>
          <p className="text-xs text-muted-foreground">Week Streak</p>
        </div>
        <div className="bg-secondary/30 rounded-xl p-3 text-center">
          <p className="text-2xl font-display font-bold text-yellow-500">{longestStreak}</p>
          <p className="text-xs text-muted-foreground">Best Streak</p>
        </div>
        <div className="bg-secondary/30 rounded-xl p-3 text-center">
          <p className="text-2xl font-display font-bold text-green-500">{weeklyStats.thisWeekWorkouts}</p>
          <p className="text-xs text-muted-foreground">This Week</p>
        </div>
      </div>

      {/* Weekly milestone indicator */}
      {weeklyStats.completedWeeks > 0 && (
        <div className="flex items-center gap-2 bg-gradient-to-r from-primary/10 to-yellow-500/10 rounded-lg px-3 py-2">
          <Star className="w-4 h-4 text-yellow-500" />
          <span className="text-sm">
            <span className="font-bold text-primary">{weeklyStats.completedWeeks} week{weeklyStats.completedWeeks !== 1 ? 's' : ''}</span>
            <span className="text-muted-foreground"> of consistent training!</span>
          </span>
        </div>
      )}

      {/* Calendar */}
      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDayClick}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          className="rounded-lg"
          components={{
            DayContent: ({ date }) => {
              const dateKey = format(date, 'yyyy-MM-dd');
              const hasWorkout = workoutDatesMap.has(dateKey);
              const workoutCount = workoutDatesMap.get(dateKey)?.length || 0;
              
              return (
                <div className="relative w-full h-full flex items-center justify-center">
                  <span>{date.getDate()}</span>
                  {hasWorkout && (
                    <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {Array.from({ length: Math.min(workoutCount, 3) }).map((_, i) => (
                        <div
                          key={i}
                          className="w-1 h-1 rounded-full bg-primary"
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            },
          }}
        />
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
          <span>Workout completed</span>
        </div>
      </div>

      {/* Day Detail Dialog */}
      <Dialog open={showDayDetail} onOpenChange={setShowDayDetail}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-primary" />
              {selectedDate && format(selectedDate, 'EEEE, MMM d, yyyy')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Workout details for the selected day
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 overflow-y-auto flex-1 pr-1">
            {selectedDateWorkouts.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No workouts on this day</p>
            ) : (
              selectedDateWorkouts.map((workout, idx) => {
                // Calculate total volume for this workout
                const totalVolume = workout.exercise_data?.reduce((acc, ex) => {
                  return acc + ex.sets.reduce((setAcc, set) => setAcc + (set.reps * set.weight), 0);
                }, 0) || 0;
                
                const totalSets = workout.exercise_data?.reduce((acc, ex) => acc + ex.sets.length, 0) || 0;
                
                return (
                  <WorkoutDetailCard
                    key={workout.id}
                    workout={workout}
                    index={idx}
                    totalWorkouts={selectedDateWorkouts.length}
                    totalVolume={totalVolume}
                    totalSets={totalSets}
                  />
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// Separate component for workout detail card with collapsible exercises
function WorkoutDetailCard({ 
  workout, 
  index, 
  totalWorkouts,
  totalVolume,
  totalSets
}: { 
  workout: WorkoutSessionRecord; 
  index: number;
  totalWorkouts: number;
  totalVolume: number;
  totalSets: number;
}) {
  const [isOpen, setIsOpen] = useState(index === 0); // First workout expanded by default

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-secondary/30 rounded-xl overflow-hidden">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
              <Dumbbell className="w-4 h-4 text-primary" />
            </div>
            <div className="text-left">
              <p className="font-medium">
                Workout {totalWorkouts > 1 ? index + 1 : ''}
              </p>
              <p className="text-xs text-muted-foreground">
                {workout.exercise_data?.filter(e => e.completed).length || workout.exercises_completed || 0} exercises completed
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {workout.duration_minutes && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {workout.duration_minutes} min
              </div>
            )}
            {isOpen ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3">
            {/* Quick Stats */}
            {(totalVolume > 0 || totalSets > 0) && (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-background/50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-primary">{totalSets}</p>
                  <p className="text-[10px] text-muted-foreground">Total Sets</p>
                </div>
                <div className="bg-background/50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-green-500">
                    {totalVolume >= 1000 ? `${(totalVolume / 1000).toFixed(1)}k` : totalVolume}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Volume (lbs)</p>
                </div>
              </div>
            )}
            
            {/* Exercise List */}
            {workout.exercise_data && workout.exercise_data.length > 0 ? (
              <div className="space-y-2">
                {workout.exercise_data.map((exercise, exIdx) => (
                  <ExerciseDetailRow key={exIdx} exercise={exercise} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">
                No detailed exercise data available
              </p>
            )}
            
            {/* Notes */}
            {workout.notes && (
              <div className="border-t border-border/30 pt-3">
                <p className="text-xs text-muted-foreground italic">
                  📝 {workout.notes}
                </p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Exercise row with expandable set details
function ExerciseDetailRow({ exercise }: { exercise: ExerciseData }) {
  const [showSets, setShowSets] = useState(false);
  
  const bestSet = exercise.sets.reduce((best, set) => {
    const volume = set.reps * set.weight;
    const bestVolume = best.reps * best.weight;
    return volume > bestVolume ? set : best;
  }, exercise.sets[0] || { reps: 0, weight: 0 });

  return (
    <div className={cn(
      "rounded-lg overflow-hidden",
      exercise.completed ? "bg-green-500/10" : exercise.skipped ? "bg-red-500/10 opacity-50" : "bg-background/30"
    )}>
      <button
        onClick={() => setShowSets(!showSets)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{exercise.name}</span>
          {exercise.skipped && (
            <span className="text-[10px] text-red-400 bg-red-500/20 px-1.5 py-0.5 rounded">Skipped</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-primary font-medium">
            {exercise.sets.length} sets
          </span>
          {showSets ? (
            <ChevronUp className="w-3 h-3 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          )}
        </div>
      </button>
      
      {showSets && exercise.sets.length > 0 && (
        <div className="px-3 pb-2 space-y-1">
          <div className="grid grid-cols-3 text-[10px] text-muted-foreground uppercase tracking-wider pb-1 border-b border-border/30">
            <span>Set</span>
            <span className="text-center">Reps</span>
            <span className="text-right">Weight</span>
          </div>
          {exercise.sets.map((set, setIdx) => {
            const isBestSet = set === bestSet && exercise.sets.length > 1;
            return (
              <div 
                key={setIdx} 
                className={cn(
                  "grid grid-cols-3 text-xs py-1",
                  isBestSet && "text-primary font-medium"
                )}
              >
                <span>#{setIdx + 1}</span>
                <span className="text-center">{set.reps}</span>
                <span className="text-right">{set.weight} lbs</span>
              </div>
            );
          })}
          {/* Best set indicator */}
          {exercise.sets.length > 1 && bestSet.weight > 0 && (
            <div className="pt-1 border-t border-border/30 flex items-center justify-between text-[10px]">
              <span className="text-muted-foreground">Best set:</span>
              <span className="text-primary font-medium">
                {bestSet.reps} × {bestSet.weight} lbs
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}